<template>
  <v-app :class="{ 'dark-mode': isDarkMode }">
    <v-app-bar
      app
      color="var(--app-navbar)"
      :light="!isDarkMode"
      :dark="isDarkMode"
      elevation="0"
      height="60"
      class="navbar"
    >
      <!-- Logo / App Name -->
      <div
        class="navbar__brand"
        @click="redirectRoute('PageConsoleDashboard')"
        @auxclick.prevent="handleAuxClick($event, 'PageConsoleDashboard')"
      >
        <span class="navbar__brand-mark">
          <v-icon color="var(--app-accent)" size="18">mdi-cube-outline</v-icon>
        </span>
        <span class="navbar__app-name">{{ $app.name }}</span>
        <span class="navbar__version">v{{ $version }}</span>
      </div>

      <!-- Hairline between the brand and the navigation path. -->
      <span class="navbar__brand-divider"></span>

      <!-- Navigation breadcrumb -->
      <v-progress-linear
        v-if="api.isLoading && services.length < 1"
        indeterminate
        color="var(--app-accent)"
        style="width: 40px"
        rounded
        class="mx-3"
      ></v-progress-linear>

      <div v-else class="navbar__center">
        <!-- Service Dropdown -->
        <!-- close-delay parks Vuetify's own hover-close out of reach on
             purpose: this menu owns a detached submenu, so closing is driven by
             handleDropdownLeave(), which can tell "left the menu" from "moved
             into the Events flyout". Zero here would let Vuetify shut the
             parent the moment the pointer heads for Events. -->
        <v-menu
          v-model="serviceMenuOpen"
          open-on-hover
          bottom
          offset-y
          content-class="navbar-dropdown"
          :close-delay="300000"
          :open-delay="0"
        >
          <template v-slot:activator="{ on, attrs }">
            <button
              class="navbar__nav-btn"
              v-bind="attrs"
              v-on="on"
              @mouseleave="handleDropdownLeave"
              @click="redirectRoute('PageServiceDashboard')"
              @auxclick.prevent="handleAuxClick($event, 'PageServiceDashboard')"
            >
              <v-icon size="16" color="var(--app-text-muted)">mdi-view-grid-outline</v-icon>
              {{ currentService ? currentService.name : "Services" }}
              <v-icon size="14" color="var(--app-text-faint)">mdi-chevron-down</v-icon>
            </button>
          </template>
          <v-list
            dense
            nav
            class="navbar-dropdown__list"
            @mouseleave.native="handleDropdownLeave"
          >
            <!-- HRDC taxPOD -->
            <template v-for="(item, index) in services">
              <v-list-item
                v-if="item.key === 'hrdc'"
                :key="'svc-' + index"
                @click="redirectRoute(item.route)"
                @auxclick.native.prevent="handleAuxClick($event, item.route)"
                :disabled="!item.isEnabled || !item.isAllowed"
                :class="{
                  'd-none': !item.isEnabled || !item.isAllowed,
                  'navbar-dropdown__item--active': item.key == $route.params.serviceKey,
                }"
                class="navbar-dropdown__item"
              >
                <v-list-item-title class="navbar-dropdown__text">{{ item.name }}</v-list-item-title>
              </v-list-item>
            </template>

            <!-- Events submenu (collapses all event modules under one item) -->
            <v-menu
              v-if="eventServices.length"
              v-model="eventsSubmenuOpen"
              open-on-hover
              offset-x
              right
              :nudge-right="0"
              :close-delay="0"
              :open-delay="0"
              content-class="navbar-dropdown navbar-dropdown--submenu"
              transition="slide-x-transition"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-list-item
                  v-bind="attrs"
                  v-on="on"
                  :class="{
                    'navbar-dropdown__item--active': isEventModuleActive,
                  }"
                  class="navbar-dropdown__item navbar-dropdown__item--has-submenu"
                >
                  <v-list-item-title class="navbar-dropdown__text">
                    Events
                  </v-list-item-title>
                  <v-icon size="16" color="var(--app-text-faint)">mdi-chevron-right</v-icon>
                </v-list-item>
              </template>
              <v-list
                dense
                nav
                class="navbar-dropdown__list"
                @mouseleave.native="handleDropdownLeave"
              >
                <v-list-item
                  v-for="(item, index) in eventServices"
                  :key="'ev-' + index"
                  @click="redirectRoute(item.route)"
                  @auxclick.native.prevent="handleAuxClick($event, item.route)"
                  :disabled="!item.isEnabled || !item.isAllowed"
                  :class="{
                    'navbar-dropdown__item--active': item.key == $route.params.serviceKey,
                    'navbar-dropdown__item--disabled': !item.isEnabled,
                  }"
                  class="navbar-dropdown__item"
                >
                  <v-list-item-title class="navbar-dropdown__text">{{ item.name }}</v-list-item-title>
                </v-list-item>

                <!-- Event Settings divider + item (super-admins only) -->
                <div
                  v-if="canViewEventSettings"
                  class="navbar-dropdown__divider-line"
                ></div>
                <v-list-item
                  v-if="canViewEventSettings"
                  @click="redirectRoute({ name: 'PageServiceDashboard', params: { serviceKey: 'hrdc_event_settings' } })"
                  @auxclick.native.prevent="handleAuxClick($event, { name: 'PageServiceDashboard', params: { serviceKey: 'hrdc_event_settings' } })"
                  :class="{
                    'navbar-dropdown__item--active': $route.params.serviceKey == 'hrdc_event_settings',
                  }"
                  class="navbar-dropdown__item navbar-dropdown__item--settings"
                >
                  <v-list-item-title class="navbar-dropdown__text">Event Settings</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <!-- Users divider -->
            <div
              v-if="canViewUsers"
              class="navbar-dropdown__divider-line"
            ></div>

            <!-- Users -->
            <template v-for="(item, index) in services">
              <v-list-item
                v-if="item.key === 'sso'"
                :key="'usr-' + index"
                @click="redirectRoute(item.route)"
                @auxclick.native.prevent="handleAuxClick($event, item.route)"
                :disabled="!item.isEnabled || !item.isAllowed"
                :class="{
                  'd-none': !item.isEnabled || !item.isAllowed,
                  'navbar-dropdown__item--active': item.key == $route.params.serviceKey,
                }"
                class="navbar-dropdown__item"
              >
                <v-list-item-title class="navbar-dropdown__text">{{ item.name }}</v-list-item-title>
              </v-list-item>
            </template>
          </v-list>
        </v-menu>

        <!-- Module Dropdown -->
        <template v-if="currentService && menu">
          <span class="navbar__separator">/</span>
          <v-menu
            open-on-hover
            bottom
            offset-y
            content-class="navbar-dropdown"
            :open-delay="0"
            :close-delay="0"
          >
            <template v-slot:activator="{ on, attrs }">
              <button class="navbar__nav-btn" v-bind="attrs" v-on="on">
                <v-icon size="16" color="var(--app-text-muted)">mdi-apps</v-icon>
                Modules
                <v-icon size="14" color="var(--app-text-faint)">mdi-chevron-down</v-icon>
              </button>
            </template>
            <v-list dense nav class="navbar-dropdown__list" :min-width="250">
              <template v-for="(item, index) in menu">
                <div :key="index">
                  <template v-if="item.type == 'divider'">
                    <v-divider class="my-1" color="grey lighten-2"></v-divider>
                  </template>
                  <v-list-item
                    v-else
                    @click="redirectRoute(item.route)"
                    @auxclick.native.prevent="handleAuxClick($event, item.route)"
                    :class="{
                      'd-none': !item.isAllowed,
                      'navbar-dropdown__item--active':
                        item.route && item.route.params && item.route.params.modelKey == $route.params.modelKey,
                    }"
                    :disabled="
                      item.isAllowed !== undefined ? !item.isAllowed : false
                    "
                    class="navbar-dropdown__item"
                  >
                    <v-list-item-icon v-if="item.icon" class="mr-3">
                      <v-icon size="18" color="var(--app-text-muted)">{{ item.icon }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-subtitle v-if="!item.route" class="navbar-dropdown__text">
                      {{ item.name }}
                    </v-list-item-subtitle>
                    <v-list-item-title v-else class="navbar-dropdown__text">
                      {{ item.name }}
                      &nbsp;
                      <v-badge
                        v-if="item.count && item.count !== undefined"
                        :content="item.count"
                        color="green"
                      ></v-badge>
                    </v-list-item-title>
                  </v-list-item>
                </div>
              </template>
            </v-list>
          </v-menu>
        </template>
      </div>

      <v-spacer />

      <!-- Dark mode toggle -->
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <button
            class="darkmode-toggle"
            :aria-label="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
            :aria-pressed="String(isDarkMode)"
            @click="toggleDarkMode()"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon size="20" :color="isDarkMode ? '#fbbf24' : '#64748b'">
              {{ isDarkMode ? 'mdi-white-balance-sunny' : 'mdi-moon-waning-crescent' }}
            </v-icon>
          </button>
        </template>
        <span>{{ isDarkMode ? 'Light Mode' : 'Dark Mode' }}</span>
      </v-tooltip>

      <!-- Right side: Profile -->
      <AppHeaderProfile @toggleLogoutDialogChild="toggleLogoutDialog" />

      <!-- Mobile menu button -->
      <button class="navbar__mobile-toggle" @click="mobileMenuOpen = !mobileMenuOpen">
        <v-icon size="22" color="var(--app-text-muted)">
          {{ mobileMenuOpen ? 'mdi-close' : 'mdi-menu' }}
        </v-icon>
      </button>
    </v-app-bar>

    <!-- Mobile slide-down menu -->
    <transition name="mobile-menu">
      <div v-if="mobileMenuOpen" class="mobile-menu" @click="mobileMenuOpen = false">
        <div class="mobile-menu__section">
          <div class="mobile-menu__label">Services</div>
          <div
            v-for="(item, index) in services"
            :key="'mob-svc-' + index"
            v-show="item.isEnabled && item.isAllowed"
            class="mobile-menu__item"
            :class="{ 'mobile-menu__item--active': item.key == $route.params.serviceKey }"
            @click="redirectRoute(item.route)"
          >
            <v-icon size="18" class="mr-3" color="var(--app-text-muted)">mdi-view-grid-outline</v-icon>
            {{ item.name }}
          </div>
        </div>
        <div v-if="currentService && menu" class="mobile-menu__section">
          <div class="mobile-menu__label">Modules</div>
          <template v-for="(item, index) in menu">
            <div :key="'mob-mod-' + index">
              <div v-if="item.type == 'divider'" class="mobile-menu__divider"></div>
              <div
                v-else-if="item.isAllowed !== false"
                class="mobile-menu__item"
                :class="{ 'mobile-menu__item--active': item.route && item.route.params && item.route.params.modelKey == $route.params.modelKey }"
                @click="item.route ? redirectRoute(item.route) : null"
              >
                <v-icon v-if="item.icon" size="18" class="mr-3" color="var(--app-text-muted)">{{ item.icon }}</v-icon>
                {{ item.name }}
                <v-badge
                  v-if="item.count && item.count !== undefined"
                  :content="item.count"
                  color="green"
                  class="ml-2"
                ></v-badge>
              </div>
            </div>
          </template>
        </div>
      </div>
    </transition>

    <v-main class="layout-main">
      <!-- Wrapper takes the slack inside v-main (which stretches to fill the
           viewport), so the footer below it always lands on the bottom edge
           instead of leaving a strip of bare main showing underneath. -->
      <div class="layout-main__content">
        <AppBreadcrumb :menu="menu" />
        <router-view :key="$route.fullPath" />

        <Survey
          :surveyModel="surveyModel"
          :surveyQuestions="surveyQuestions"
          :surveyDetails="surveyDetails"
          :surveyAnswer="surveyAnswer"
          :errorField="errorField"
          @toggleSurveyDialogChildren="toggleSurveyDialog"
          @submitFormChildren="submitForm"
        ></Survey>
      </div>

      <!-- Last thing in main: nothing may follow it, or that node's box shows up
           as a strip under the bar. -->
      <AppFooter />
    </v-main>

    <TheModalConfirmation
      ref="confirmPopup"
      @agree="agreeCallback"
      @disagree="disagreeCallback"
    ></TheModalConfirmation>

    <WelcomeSplash />
  </v-app>
</template>

<script>
import Api from "@/objects/api";
import { mapState } from "vuex";
import configMenu from "@/config/menu";
import hrdcFunction from "@/objects/hrdcFunction";
import Survey from "@/components/views/v1/Survey";
import AppHeaderProfile from "@/components/layouts/AppHeaderProfile";
import AppBreadcrumb from "@/components/layouts/AppBreadcrumb.vue";
import TheModalConfirmation from "@/components/common/TheModalConfirmation.vue";
import WelcomeSplash from "@/components/common/WelcomeSplash.vue";
import { themeState, toggleDark } from "@/plugins/theme";

export default {
  mixins: [hrdcFunction],
  components: {
    Survey,
    AppHeaderProfile,
    AppBreadcrumb,
    TheModalConfirmation,
    WelcomeSplash,
  },
  computed: {
    // Reads the app-wide theme singleton rather than local state, so the bar
    // reflects the theme even when it was flipped from somewhere else.
    isDarkMode() {
      return themeState.dark;
    },
    ...mapState({
      auth: (state) => state.auth.data,
      debugMode: (state) => state.debugMode.data,
      services: (state) => state.service.data.services,
      currentService: (state) => state.service.data.currentService,
    }),
    eventServices() {
      if (!this.services) return [];
      return this.services.filter(
        (s) =>
          s.key !== "hrdc" &&
          s.key !== "sso" &&
          s.key !== "hrdc_event_settings" &&
          s.key.startsWith("hrdc_") &&
          s.isEnabled !== false
      );
    },
    isEventModuleActive() {
      const key = this.$route.params.serviceKey;
      return key && key !== "hrdc" && key !== "sso" && key.startsWith("hrdc_");
    },
    // Same gate as "Users" navbar item — super-admins only. Inlined here
    // (rather than relying on the mixin's flag) because LayoutConsoleV2
    // doesn't call rolesAssignation(), so isHrdcSuperAdminRoles stays false.
    canViewEventSettings() {
      const slugs = this.$_.map((this.auth && this.auth.roles) || [], "slug");
      return (this.isHrdcSuperAdmin || []).some((s) => slugs.includes(s));
    },
    // Whether the "Users" (sso) item is actually visible. Used to avoid
    // rendering a dangling divider line when the role can't access it.
    canViewUsers() {
      if (!this.services) return false;
      const sso = this.services.find((s) => s.key === "sso");
      return !!(sso && sso.isEnabled && sso.isAllowed);
    },
  },
  data: () => ({
    mobileMenuOpen: false,
    eventGroupOpen: true,
    eventSubMenuOpen: false,
    serviceMenuOpen: false,
    eventsSubmenuOpen: false,
    api: new Api(),
    apiGetUser: new Api(),
    apiGetSurveyQuestion: new Api(),
    apiSubmit: new Api(),
    menu: null,
    serviceKey: null,
    confirmationTitle: "Logout",
    confirmationMessage: "Are you sure to logout?",
    surveyModel: false,
    surveyQuestions: [],
    surveyDetails: {},
    surveyAnswer: {},
    errorField: {},
  }),
  watch: {
    "$route.params.serviceKey": {
      handler: function (newServiceKey, oldServiceKey) {
        if (newServiceKey != oldServiceKey) {
          this.$store.dispatch(
            "selectCurrentServiceByServiceKey",
            newServiceKey
          );
          if (newServiceKey) this.updateMenu(newServiceKey);
          this.serviceKey = newServiceKey;
        }
      },
      deep: true,
      immediate: true,
    },
  },
  created() {
    const apiDetails = {
      api: {
        url: `${this.$service.hrdc}/v1/en/console/dashboard`,
        method: "post",
      },
      apiGetUser: {
        url: `${this.$service.sso}/v1/en/auth/me`,
      },
      apiGetSurveyQuestion: {
        url: `${this.$service.hrdc}/v1/en/console/survey/get-survey-by-uuid`,
      },
      apiSubmit: {
        url: `${this.$service.hrdc}/v1/en/console/survey`,
        method: "post",
      },
    };

    for (const api in apiDetails) {
      if (apiDetails[api].url !== undefined) {
        this[api].setUrl(apiDetails[api].url);
      }
      if (apiDetails[api].method !== undefined) {
        this[api].setMethod(apiDetails[api].method);
      }
      if (apiDetails[api].params !== undefined) {
        this[api].setParams(apiDetails[api].params);
      }
    }

    this.apiGetUser.setCallbackCompleted((response) => {
      try {
        const { status, data } = response;
        if (status) {
          this.$store.commit("updateAuth", data);
          this.api.fetch();
        }

        if (!status) {
          this.$router.push({ name: "PageConsoleLogout" });
        }
      } catch (err) {
        console.log(err);
      }
    });
    this.apiGetUser.fetch();

    var internalRoleSlugs = this.$_.map(this.auth.roles, "slug");
    this.api.setParams({
      internalRoleSlugs: JSON.stringify(internalRoleSlugs),
    });
    this.api.setCallbackCompleted((response) => {
      var services = response;

      // For event-admin role: allow all modules except HRDC & SSO (Users).
      var roleSlugs = this.$_.map(this.auth.roles, "slug");
      var hasOnlyEventAdmin =
        roleSlugs.length === 1 && roleSlugs[0] === "event-admin";
      if (hasOnlyEventAdmin) {
        services = services.map((service) => {
          if (service.key === "hrdc" || service.key === "sso") {
            return { ...service, isAllowed: false };
          }
          return { ...service, isAllowed: true };
        });
      }

      // Remove all hrdc_ event modules from CRM response (except "hrdc" taxPOD)
      // Event modules are sourced ONLY from the EventModule table via the registry
      services = services.filter((s) => {
        return s.key === "hrdc" || s.key === "sso" || !s.key.startsWith("hrdc_");
      });

      // Insert ALL event modules from the registry (EventModule table) before Users (sso)
      const { getAllEventConfigsIncludingInactive } = require("@/services/hrdc_event/eventRegistry");
      const eventConfigs = getAllEventConfigsIncludingInactive();
      for (const config of eventConfigs) {
        const ssoIndex = services.findIndex((s) => s.key === "sso");
        const insertAt = ssoIndex !== -1 ? ssoIndex : services.length;
        services.splice(insertAt, 0, {
          key: config.serviceKey,
          name: config.appName,
          description: `Manage ${config.appName} Application`,
          isActive: config.isActive !== false,
          isEnabled: config.isActive !== false,
          isAllowed: true,
        });
      }

      // Add "Event Settings" card for shared global settings
      if (eventConfigs.length) {
        const ssoIndex = services.findIndex((s) => s.key === "sso");
        const insertAt = ssoIndex !== -1 ? ssoIndex : services.length;
        services.splice(insertAt, 0, {
          key: "hrdc_event_settings",
          name: "Event Settings",
          description: "Event Module management",
          isActive: true,
          isEnabled: true,
          isAllowed: true,
        });
      }

      this.$store.dispatch("updateServices", services);
    });

    // Master switch for survey launches. When VUE_APP_SURVEY !== "true", surveys
    // are turned off entirely: we don't hit get-active-survey and never pop the
    // dialog. Flip it to "true" in the env to launch surveys.
    if (process.env.VUE_APP_SURVEY === "true") {
      // Show whichever survey is currently live — active and within its date
      // window. The backend returns null when no survey is live.
      this.apiGetSurveyQuestion.setUrl(
        `${this.$service.hrdc}/v1/en/console/survey/get-active-survey`
      );
      this.apiGetSurveyQuestion.setQuery(`userUuid=${this.auth.uuid}`);

      this.apiGetSurveyQuestion.setCallbackCompleted((response) => {
        try {
          const { status, data } = response;
          // `data` is null when no survey is currently live.
          if (status && data && data.SurveyResult.length == 0) {
            this.surveyModel = true;
            this.surveyDetails = {
              uuid: data.uuid,
              title: data.title,
              attachment: data.attachment,
              memorandum: data.memorandum,
            };
            this.surveyQuestions = data.SurveyQuestion;

            for (const question of data.SurveyQuestion) {
              const fieldType = this.assignAnswerFieldType(question.answerType);
              this.$set(this.surveyAnswer, question.uuid, fieldType);
              this.$set(this.errorField, question.uuid, null);
            }
          }
        } catch (err) {
          console.log(err);
        }
      });

      this.apiGetSurveyQuestion.fetch();
    }
  },
  methods: {
    toggleDarkMode() {
      // Persistence, the <html> class and $vuetify.theme.dark all live in
      // src/plugins/theme.js so the auth pages and developer console — which
      // never render this layout — stay in step.
      toggleDark();
    },
    updateMenu(serviceKey) {
      // Event Settings has its own menu
      if (serviceKey === "hrdc_event_settings") {
        const { getAllEventConfigs } = require("@/services/hrdc_event/eventRegistry");
        const firstConfig = getAllEventConfigs()[0];
        let targetMenu = [
          // XERO Token moved to the Developer Console (/en/developer).
          {
            name: "Event Modules",
            icon: "mdi-puzzle-outline",
            route: firstConfig
              ? { name: "HrdcEventSettingsEventModules", params: { serviceKey: "hrdc_event_settings" } }
              : { name: "PageServiceDashboard" },
          },
        ];
        this.menu = this.$_.clone(targetMenu);
        this.menu.push({ type: "divider" });
        this.menu.push({
          name: "Dashboard",
          route: { name: "PageServiceDashboard", params: { serviceKey } },
        });
        const data = { roles: this.auth.roles, menu: this.menu };
        this.configMenu(data);
        return;
      }

      // First check static menu config, then try dynamic event module menu
      let targetMenu = configMenu[serviceKey];
      if (!targetMenu) {
        const { getEventConfig, generateMenuConfig } = require("@/services/hrdc_event/eventRegistry");
        const eventConfig = getEventConfig(serviceKey);
        if (eventConfig) {
          targetMenu = generateMenuConfig(eventConfig);
        }
      }
      if (!targetMenu) {
        this.menu = null;
        return;
      }
      this.menu = this.$_.clone(targetMenu);
      this.menu.push({
        type: "divider",
      });
      this.menu.push({
        name: "Dashboard",
        route: {
          name: "PageServiceDashboard",
          params: { serviceKey: serviceKey },
        },
      });

      const data = {
        roles: this.auth.roles,
        menu: this.menu,
      };

      this.configMenu(data);
    },
    closeServiceMenu() {
      this.eventSubMenuOpen = false;
      this.serviceMenuOpen = false;
      this.eventsSubmenuOpen = false;
      if (this.$refs.serviceMenu) {
        this.$refs.serviceMenu.isActive = false;
      }
    },
    // Mouse left the trigger or one of the panels — close NOW, with no grace
    // period, unless the pointer went straight into another panel of the same
    // menu. That exception is what the delay used to buy: the Events flyout is
    // its own detached v-menu, so moving into it registers as leaving this one,
    // and a blind close would kill the menu the user is reaching for.
    // Asking where the pointer actually went is exact where a timer was only a
    // guess — and it means an honest exit costs nothing. The panels' CSS hover
    // bridges cover the offsets in between, so `relatedTarget` lands on a panel
    // rather than on the page while crossing.
    handleDropdownLeave(event) {
      const to = event && event.relatedTarget;
      if (
        to &&
        typeof to.closest === "function" &&
        to.closest(".navbar-dropdown, .navbar__nav-btn")
      ) {
        return;
      }
      this.closeServiceMenu();
    },
    redirectRoute(routeName) {
      // Force the navbar dropdown closed on every nav so the menu doesn't
      // hang around after the user picks a destination.
      this.closeServiceMenu();
      switch (routeName) {
        case "PageServiceDashboard":
          if (
            this.currentService &&
            this.serviceKey !== this.currentService.key
          ) {
            this.$router.push({
              name: routeName,
              params: { serviceKey: this.currentService.key },
            }).catch(() => {});
          }
          break;

        case "PageConsoleDashboard":
          if (this.$route.name !== routeName) {
            this.$router.push({ name: routeName }).catch(() => {});
          }
          break;

        default:
          this.$router.push({
            name: routeName.name,
            params: routeName.params,
            query: routeName.query,
          }).catch(() => {});
          break;
      }
    },
    // Middle-click handler — opens the same destination in a new tab.
    // Bound via @auxclick.native.prevent so the browser doesn't show the
    // autoscroll cursor and so the dropdown's @click doesn't also fire.
    handleAuxClick(event, routeName) {
      if (event.button !== 1) return;
      this.openInNewTab(routeName);
    },
    openInNewTab(routeName) {
      // Resolve a navbar route to a fresh-loadable URL, then open in a new
      // tab. We can't rely on vue-router's automatic param inheritance here
      // because new tabs do a full page load — every required path param has
      // to be present in the generated URL.
      const fallbackServiceKey =
        (this.$route && this.$route.params && this.$route.params.serviceKey) ||
        (this.currentService && this.currentService.key) ||
        null;

      // PageServiceLanding is a redirect-only record (-> /service/:key/dashboard).
      // vue-router 3 doesn't follow redirects in resolve(), so a fresh load of
      // its href lands on a route with no inner component — manifesting as a
      // navbar-only page. Normalize it to the actual destination.
      const normalizeName = (name) =>
        name === "PageServiceLanding" ? "PageServiceDashboard" : name;

      let resolved = null;
      try {
        if (routeName === "PageServiceDashboard") {
          if (this.currentService) {
            resolved = this.$router.resolve({
              name: routeName,
              params: { serviceKey: this.currentService.key },
            });
          }
        } else if (routeName === "PageConsoleDashboard") {
          resolved = this.$router.resolve({ name: routeName });
        } else if (routeName && typeof routeName === "object" && routeName.name) {
          const params = { ...(routeName.params || {}) };
          if (!params.serviceKey && fallbackServiceKey) {
            params.serviceKey = fallbackServiceKey;
          }
          resolved = this.$router.resolve({
            name: normalizeName(routeName.name),
            params,
            query: routeName.query,
          });
        }
      } catch (err) {
        console.warn("[navbar] openInNewTab: route resolve failed", err);
        return;
      }

      if (!resolved || !resolved.href || resolved.href === "/") {
        console.warn("[navbar] openInNewTab: empty/invalid href", { routeName, resolved });
        return;
      }
      window.open(resolved.href, "_blank");
    },
    agreeCallback() {
      this.$router.push({
        name: "PageConsoleLogout",
      });
    },
    disagreeCallback() {
      //
    },
    toggleLogoutDialog(val) {
      if (val) {
        this.$refs.confirmPopup.open({
          title: this.confirmationTitle,
          message: this.confirmationMessage,
          options: {
            btnDisagree: "Cancel",
            btnAgree: "Logout",
          },
          agree: () => {
            this.agreeCallback();
          },
          disagree: () => {
            this.disagreeCallback();
          },
        });
      }
    },
    toggleSurveyDialog(val) {
      this.surveyModel = val;
    },
    assignAnswerFieldType(answerType) {
      let fieldType = null;
      switch (answerType) {
        case "checkbox":
          fieldType = [];
          break;
        default:
        case "radio":
        case "textarea":
          break;
      }

      return fieldType;
    },

    submitForm(answers) {
      this.apiSubmit.setParams({
        answers,
        userUuid: this.auth.uuid,
        // Submit against the survey that was actually shown.
        surveyUuid: this.surveyDetails.uuid,
      });

      this.apiSubmit.setCallbackCompleted((response) => {
        try {
          const { status, data } = response;
          if (!status) {
            this.$store.dispatch("showMessage", {
              message: data,
              messageType: "error",
            });
          }

          if (status) {
            this.$store.dispatch("showMessage", {
              message: data,
              messageType: "success",
            });
          }
        } catch (err) {
          console.log(err);
        }
      });
      this.apiSubmit.fetch();
    },
  },
};
</script>

<style>
/* The auto-dark companion block that used to sit here is gone on purpose.
   Every navbar / dropdown / mobile-menu rule below now resolves its colours
   through var(--app-*), which already flip with `.dark-mode` on <html> — so a
   second set of `.dark-mode .navbar…` rules would only restate them, and (as
   happened before) silently outrank the glass background with an opaque one
   just by appearing later in the file. The dark palette lives in
   src/plugins/theme-dark.css, and the shared surfaces every page draws on live
   in src/plugins/theme-glass.css. Nothing in this file needs a theme-specific
   selector any more — there is not one `.dark-mode` rule left in it. */

/* ============================================
   NAVBAR — floating glass bar
   --------------------------------------------
   The bar is detached from the viewport edges and painted as a translucent
   sheet, so page content scrolls UNDERNEATH it and is blurred rather than
   clipped by an opaque band.

   THREE NUMBERS HAVE TO AGREE, so they are kept together here:

       bar height      60px   (the `height` prop on <v-app-bar>)
       bar inset       10px top, 14px sides
       v-main padding  76px   = 10 + 60 + 6

   The padding is what keeps the first row of page content clear of the bar's
   bottom edge on a fresh load. Change one number and change all three, or
   content either hides under the bar or leaves a gap below it. The mobile
   block at the bottom of this file restates the same three for a 56px bar.

   WHY SO MANY !important: Vuetify's application layout writes `height`,
   `left`, `right` and the v-main `padding` as INLINE styles on these
   elements. These rules are overriding an inline style, not winning a
   specificity fight with another stylesheet.
   ============================================ */
.navbar.v-app-bar {
  top: 10px !important;
  left: 14px !important;
  right: 14px !important;
  width: auto !important;
  max-width: calc(100% - 28px) !important;
  border-radius: 18px !important;
  overflow: hidden;
  /* Opaque by default — see the @supports note below. */
  background: var(--app-navbar) !important;
  border: 1px solid var(--app-navbar-glass-border) !important;
  box-shadow:
    0 6px 24px var(--app-navbar-shadow),
    inset 0 1px 0 var(--app-inner-highlight) !important;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif !important;
}

/* Glass is a progressive enhancement, not the baseline. Without
   backdrop-filter a half-alpha sheet doesn't read as frosted — the page just
   bleeds through as noise behind the labels — so browsers that can't blur
   keep the fully opaque bar above and lose nothing but the effect.

   WHAT MAKES IT READ AS GLASS AND NOT AS A WEAK PANEL
   ---------------------------------------------------
   Four things, and dropping any one of them takes the material with it:

     fill     --app-chrome-glass, thinner than the page sheet's
              --app-navbar-glass — see the token's note in theme-dark.css for
              why the chrome gets its own pair rather than a lower alpha on the
              shared one.
     blur     --glass-blur-chrome, the app's widest. The thinner the fill, the
              wider the radius has to be, or what scrolls under the bar stays
              legible and fights the labels sitting on it.
     sheen    a white fall-off from the top edge, painted as a background-image
              LAYER over the fill (comma-separated, gradient first — the fill
              is the bottom layer). This is the light on the face of the sheet;
              it is what a flat translucent rectangle is missing.
     edge     ONE line: the 1px border, in --app-chrome-glass-border. Not
              --app-navbar-glass-border, which is cut for the page sheet's much
              thicker fill and lands 17 levels brighter than this one — a
              bright ring around a visibly greyer middle, which is a second
              container, not an edge.

   There is no lit-rim / shaded-rim pair. Two earlier attempts at one — inset
   shadows, then background hairlines — both put a second contour just inside
   the border and both got reported as "the navbar looks like two stacked
   containers". theme-glass.css carries the post-mortem; the short version is
   that an edge the eye can follow twice is an edge too many.

   brightness() on the backdrop is the fourth quietly doing the most work: it
   lifts what shows through slightly above the page's own luminance, so the
   sheet reads as a surface CATCHING light rather than as a hole cut into the
   bar. */
@supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  .navbar.v-app-bar {
    background:
      /* `no-repeat` is load-bearing, not tidiness. background-origin is
         padding-box, so an unsized gradient layer is 60px tall starting 1px
         BELOW the border-box top — and background-repeat defaults to `repeat`,
         which tiled it. The second tile's first row is the gradient's 0% stop,
         0.55 white, and it landed exactly in the bottom 1px border strip: a
         bright hairline along the bottom edge that read as a seam under the
         bar. Sizing it to the box and switching repeat off keeps the sheen a
         fall-off from the top and nothing else. The dropdown and the mobile
         sheet below already state both, which is why only the bar had it. */
      var(--app-glass-sheen) top / 100% 100% no-repeat,
      var(--app-chrome-glass) !important;
    /* Not --app-navbar-glass-border: that one is cut for the page sheet's much
       thicker fill and reads as a bright ring on this one. See the token. */
    border-color: var(--app-chrome-glass-border) !important;
    -webkit-backdrop-filter:
      blur(var(--glass-blur-chrome)) saturate(var(--glass-saturate))
      brightness(var(--app-glass-brightness));
    backdrop-filter:
      blur(var(--glass-blur-chrome)) saturate(var(--glass-saturate))
      brightness(var(--app-glass-brightness));
    box-shadow: 0 6px 24px var(--app-navbar-shadow) !important;
  }
}

.navbar .v-toolbar__content {
  align-items: center !important;
  height: 100% !important;
  padding: 0 10px !important;
}

/* ===== Brand ===== */
.navbar__brand {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 5px 10px 5px 5px;
  border-radius: 12px;
  transition: background-color 0.15s ease;
}

.navbar__brand:hover {
  background-color: var(--app-hover);
}

/* The icon gets its own tile so the accent reads as a mark rather than as a
   loose blue glyph floating next to the wordmark. */
.navbar__brand-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 9px;
  background: var(--app-accent-soft);
  border: 1px solid var(--app-accent-border);
}

.navbar__app-name {
  font-size: 0.95rem;
  font-weight: 800;
  color: var(--app-text);
  letter-spacing: -0.2px;
  line-height: 1;
}

.navbar__version {
  font-size: 0.6rem;
  font-weight: 700;
  color: var(--app-text-faint);
  margin-left: 2px;
  background: var(--app-hover);
  padding: 2px 6px;
  border-radius: 5px;
  line-height: 1;
}

.navbar__brand-divider {
  width: 1px;
  height: 22px;
  background: var(--app-border);
  margin: 0 10px 0 4px;
  flex: 0 0 auto;
}

/* ===== Nav Center ===== */
.navbar__center {
  display: flex;
  align-items: center;
  gap: 2px;
  min-width: 0;
}

.navbar__separator {
  color: var(--app-text-faint);
  font-size: 0.9rem;
  margin: 0 2px;
}

.navbar__nav-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 13px;
  border: none;
  background: transparent;
  /* Pill, not a rounded rect: on a glass sheet a soft hover wash with square-ish
     corners reads as a second panel rather than as a control. */
  border-radius: 999px;
  cursor: pointer;
  font-family: 'Lato', sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--app-text-muted);
  white-space: nowrap;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.navbar__nav-btn:hover,
/* Vuetify puts aria-expanded on the menu activator, so the trigger stays lit
   for as long as its dropdown is open. */
.navbar__nav-btn[aria-expanded="true"] {
  background-color: var(--app-hover);
  color: var(--app-text);
}

.navbar__nav-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--app-ring);
}

/* ===== Dropdown ===== */
/* WHY EVERY SELECTOR IN THIS SECTION CARRIES `html …` AND A DOUBLED CLASS
   -----------------------------------------------------------------------
   Twelve page files (hrdc + hrdc_event application/Index.vue, claim_approved,
   refund_email_*, the developer Xero/Job modules, …) still carry an UNSCOPED
   <style> block with auto-generated rules of the form
       .dark-mode .v-menu__content        { background: var(--app-card) !important }
       .dark-mode .v-menu__content .v-list{ background: var(--app-card) !important }
   Unscoped means global: the moment one of those pages mounts, its CSS is
   injected and repaints EVERY menu in the app — this navbar's included. At
   (0,3,0) + !important they outranked the plain `.navbar-dropdown__list`
   (0,1,0) below, so the v-list repainted itself as an opaque, SQUARE-cornered
   slab of --app-card across the whole panel. Nothing clipped it, because this
   panel deliberately runs `overflow: visible` for its hover bridge — so the
   slab's corners stuck out past the rounded panel. That is the "corner out of
   the box" in dark mode, and it appeared only on pages carrying one of those
   blocks, which is why the same menu looked right elsewhere.
   `html` + the doubled class puts these rules at (0,3,1) and up, out of reach
   of anything a page can inject. Keep the prefix when adding a rule here. */
/* Same material as the bar: opaque baseline, frosted where supported. The
   v-list inside is forced transparent — it is a v-sheet and paints its own
   theme background, which would sit as an opaque slab on top of the glass. */
html .navbar-dropdown.v-menu__content {
  border-radius: 14px !important;
  background: var(--app-navbar) !important;
  border: 1px solid var(--app-border);
  box-shadow:
    0 12px 34px var(--app-navbar-shadow),
    inset 0 1px 0 var(--app-inner-highlight) !important;
  margin-top: 6px !important;
  /* Vuetify paints `.v-menu__content` with `overflow-y: auto` AND
     `contain: content`. Both clip the panel to its own box, and paint
     containment also swallows its pseudo-elements — so the hover bridge below,
     which by definition has to sit ABOVE that box, was being clipped out of
     existence and never received the pointer at all. Neither declaration is
     load-bearing here: these menus set no max-height, so Vuetify repositions
     them to fit the viewport rather than scrolling them (see calcYOverflow in
     mixins/menuable), and `contain` is only a paint hint. */
  overflow: visible !important;
  contain: none !important;
}

/* Same material as the bar, one notch denser: a menu COVERS content instead of
   floating over it, and carries the smallest text in the chrome — so the fill
   is --app-chrome-menu-glass, not the bar's. Everything else is the bar's
   recipe verbatim; see its note above for what each layer is doing. */
@supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  html .navbar-dropdown.v-menu__content {
    /* The sheen is pinned to 90px rather than left at 100% height: its stops
       are percentages, so on a long menu the top-edge highlight stretches into
       a white wash halfway down the panel. A pane of glass catches light at
       its EDGE — that distance doesn't grow with the panel. */
    background:
      var(--app-glass-sheen) top center / 100% 90px no-repeat,
      var(--app-chrome-menu-glass) !important;
    -webkit-backdrop-filter:
      blur(var(--glass-blur-chrome)) saturate(var(--glass-saturate))
      brightness(var(--app-glass-brightness));
    backdrop-filter:
      blur(var(--glass-blur-chrome)) saturate(var(--glass-saturate))
      brightness(var(--app-glass-brightness));
    border-color: var(--app-chrome-glass-border);
    box-shadow: 0 12px 34px var(--app-navbar-shadow) !important;
  }
}

/* The submenu flies out sideways, so its offset is horizontal, not vertical. */
html .navbar-dropdown.navbar-dropdown--submenu {
  margin-top: 0 !important;
  margin-left: 6px !important;
}

/* HOVER BRIDGE — depends on the overflow/contain overrides above; it is inert
   without them.
   Leaving the activator schedules an unconditional close (activatable's
   mouseleave calls runDelay('close') with no callback), and the only thing that
   cancels it is a mouseenter on the panel. Those two fire in the SAME task when
   the boxes touch — so the close is cleared before its timer can run, and no
   grace period is needed. The 6px offset that floats the panel off the bar is
   what broke that: it left a strip belonging to neither box, so a pointer
   sampled inside it closed the menu on the way in.
   This fills the strip as an invisible part of the panel. It spans the margin
   PLUS the panel's 1px border, because `top` is resolved against the padding
   box — at -6px it stopped 1px short of the button and the seam still leaked. */
html .navbar-dropdown.v-menu__content::before {
  content: "";
  position: absolute;
  top: -7px; /* 6px margin-top + the 1px border above the padding box */
  left: -1px;
  right: -1px;
  height: 7px;
}

/* Sideways flyout: same bridge, rotated onto the left edge. */
html .navbar-dropdown.navbar-dropdown--submenu::before {
  top: -1px;
  bottom: -1px;
  left: -7px; /* == margin-left + border */
  right: auto;
  width: 7px;
  height: auto;
}

html .navbar-dropdown.v-menu__content .navbar-dropdown__list {
  padding: 6px !important;
  background: transparent !important;
  /* A guard, not decoration. The panel above runs `overflow: visible` so its
     hover bridge can escape the box — which also means it can no longer clip
     its own children to its 14px radius. Anything opaque in here (Vuetify's
     own sheet background, or a global dark-mode rule outranking the
     `transparent` above, which is exactly what happened once) then paints a
     square-cornered slab whose corners stick out past the rounded panel. The
     list fills the panel's padding box, so clipping it at 13px — 14px minus
     the panel's 1px border — restores the corner regardless. */
  border-radius: 13px;
  overflow: hidden;
}

html .navbar-dropdown.v-menu__content .navbar-dropdown__divider-label {
  font-size: 0.68rem;
  font-weight: 700;
  color: var(--app-text-faint);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 8px 12px 4px;
  border-top: 1px solid var(--app-border);
  margin-top: 4px;
}

html .navbar-dropdown.v-menu__content .navbar-dropdown__divider-line {
  border-top: 1px solid var(--app-border);
  margin: 5px 6px;
}

html .navbar-dropdown.v-menu__content .navbar-dropdown__item {
  border-radius: 10px !important;
  min-height: 38px !important;
  padding: 0 12px !important;
  margin-bottom: 2px;
  transition: background-color 0.12s ease;
}

html .navbar-dropdown.v-menu__content .navbar-dropdown__item:hover {
  background-color: var(--app-hover) !important;
}

html .navbar-dropdown.v-menu__content .navbar-dropdown__item--active {
  background-color: var(--app-accent-soft) !important;
}

html .navbar-dropdown.v-menu__content .navbar-dropdown__item--disabled {
  opacity: 0.5;
}

html .navbar-dropdown.v-menu__content .navbar-dropdown__text {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.82rem !important;
  font-weight: 500 !important;
  color: var(--app-text) !important;
}

html .navbar-dropdown.v-menu__content .navbar-dropdown__item:hover .navbar-dropdown__text {
  color: var(--app-accent) !important;
}

html .navbar-dropdown.v-menu__content .navbar-dropdown__item--active .navbar-dropdown__text {
  color: var(--app-accent-strong) !important;
  font-weight: 600 !important;
}

html .navbar-dropdown.v-menu__content .navbar-dropdown__list .v-divider {
  border-color: var(--app-border) !important;
}

/* ===== Layout Main ===== */
.layout-main {
  /* Colour comes from --app-ground, applied in theme-glass.css. Only the
     geometry that depends on the bar's height is this file's business:
     10 (inset) + 60 (bar) + 6 (clearance) — see the contract at the top of this
     block. Overrides Vuetify's inline `padding: 60px 0 0`. */
  padding-top: 76px !important;
}

/* v-main is a flex row whose single child .v-main__wrap fills it; turning the
   wrap into a column with the content set to grow puts AppFooter after the page
   content AND flush with the bottom of main on short pages. Not scoped: these
   class names are Vuetify's own. */
.layout-main > .v-main__wrap {
  display: flex;
  flex-direction: column;
}

/* Column so a routed page that wants to fill the viewport can say `flex: 1 0
   auto` instead of guessing at `100vh - chrome` — the breadcrumb and footer are
   real boxes here, and a vh-based guess that ignores them overflows the viewport
   and leaves a permanent scrollbar. */
.layout-main__content {
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
}

/* ===== Icon buttons in the bar (dark-mode toggle, hamburger) ===== */
.darkmode-toggle,
.navbar__mobile-toggle {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease;
  flex: 0 0 auto;
}

.darkmode-toggle {
  margin-right: 4px;
}

.darkmode-toggle:hover,
.navbar__mobile-toggle:hover {
  background-color: var(--app-hover);
}

.darkmode-toggle:focus-visible,
.navbar__mobile-toggle:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--app-ring);
}

/* Hidden on desktop; the media query below turns it back on. */
.navbar__mobile-toggle {
  display: none;
}

/* ===== Mobile slide-down menu ===== */
/* Inset and rounded to match the floating bar rather than running edge to
   edge — a full-bleed sheet under a floating bar reads as two unrelated
   surfaces. */
.mobile-menu {
  position: fixed;
  top: 72px;
  left: 8px;
  right: 8px;
  bottom: 8px;
  background: var(--app-navbar);
  border: 1px solid var(--app-navbar-glass-border);
  border-radius: 18px;
  box-shadow: 0 10px 30px var(--app-navbar-shadow);
  z-index: 99;
  overflow-y: auto;
  padding: 10px 6px;
  font-family: 'Lato', sans-serif;
}

@supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  .mobile-menu {
    /* Capped for the same reason as the dropdown's — and harder, because this
       sheet runs the full height of the viewport. */
    background:
      var(--app-glass-sheen) top center / 100% 120px no-repeat,
      var(--app-chrome-menu-glass);
    border-color: var(--app-chrome-glass-border);
    -webkit-backdrop-filter:
      blur(var(--glass-blur-chrome)) saturate(var(--glass-saturate))
      brightness(var(--app-glass-brightness));
    backdrop-filter:
      blur(var(--glass-blur-chrome)) saturate(var(--glass-saturate))
      brightness(var(--app-glass-brightness));
    box-shadow: 0 10px 30px var(--app-navbar-shadow);
  }
}

.mobile-menu__section {
  padding: 6px 10px;
}

.mobile-menu__label {
  font-size: 0.68rem;
  font-weight: 700;
  color: var(--app-text-faint);
  text-transform: uppercase;
  letter-spacing: 1.5px;
  padding: 8px 12px 6px;
}

.mobile-menu__item {
  display: flex;
  align-items: center;
  padding: 12px;
  border-radius: 12px;
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--app-text);
  cursor: pointer;
  transition: background 0.12s;
}

.mobile-menu__item:hover {
  background: var(--app-hover);
}

.mobile-menu__item--active {
  background: var(--app-accent-soft);
  color: var(--app-accent-strong);
  font-weight: 600;
}

.mobile-menu__divider {
  height: 1px;
  background: var(--app-border);
  margin: 6px 12px;
}

/* Mobile menu transition */
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: all 0.25s ease;
}

.mobile-menu-enter,
.mobile-menu-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* ===== MOBILE RESPONSIVE ===== */
@media (max-width: 599px) {
  /* The same three numbers as the desktop contract, one size down:
     56px bar, 8px inset, 70px of main padding. */
  .navbar.v-app-bar {
    height: 56px !important;
    top: 8px !important;
    left: 8px !important;
    right: 8px !important;
    max-width: calc(100% - 16px) !important;
    border-radius: 14px !important;
  }

  .navbar .v-toolbar__content {
    padding: 0 6px !important;
  }

  .layout-main {
    padding-top: 70px !important;
  }

  .mobile-menu {
    top: 68px;
  }

  .navbar__app-name {
    font-size: 0.82rem;
  }

  .navbar__version,
  .navbar__brand-divider {
    display: none;
  }

  /* Hide desktop nav, show hamburger */
  .navbar__center {
    display: none !important;
  }

  .navbar__mobile-toggle {
    display: flex;
  }

  .darkmode-toggle,
  .navbar__mobile-toggle {
    width: 34px;
    height: 34px;
  }

  .darkmode-toggle {
    margin-right: 0;
  }

  .profile-name {
    display: none !important;
  }

  .profile-btn {
    padding: 4px;
    gap: 4px;
  }
}

@media (min-width: 600px) and (max-width: 959px) {
  .navbar__version {
    display: none;
  }

  .navbar__nav-btn {
    font-size: 0.78rem;
    padding: 6px 10px;
  }
}

/* ===== Tooltip Override ===== */
.v-tooltip__content {
  background: #1e293b !important;
  color: #f1f5f9 !important;
  font-family: 'Lato', sans-serif !important;
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  padding: 8px 14px !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15) !important;
  opacity: 1 !important;
  transition: opacity 0s !important;
}

/* The dark tooltip is owned by src/plugins/theme-dark.css, which gives it the
   elevated surface + border every other floating panel gets. This used to
   invert it to a near-white chip, which on the new near-black page read as a
   flare rather than a tooltip — and duplicating the rule here meant whichever
   stylesheet the bundler emitted last won. One definition, globally. */

/* ===== Active list item override ===== */
.v-list-item--active {
  background-image: none;
}

/* ============================================
   DARK MODE OVERRIDES
   ============================================ */

/* The navbar, its dropdowns, the dark-mode toggle and the profile menu used to
   be re-declared here for dark. They aren't any more — every one of those rules
   is token-driven now, in this file for the bar and in AppHeaderProfile.vue for
   the profile button and its card (whose <style> is deliberately NOT scoped, so
   it already reaches the menu Vuetify detaches out of that component).

   That duplication was not harmless: an opaque `background: var(--app-page)`
   restated at this point in the file outranks the sheet's own rule by
   specificity, and would flatten the frosted bar and menus back into solid
   slabs in dark mode only. The rules that remain below are page-content rules,
   which have no token-driven light counterpart to inherit from. */

/* ============================================
   PAGE CONTENT
   ============================================
   Everything that used to live here — the view page, section cards and their
   headers, detail rows, badges, the remarks table, the action dropdown and the
   Vuetify surface overrides — has moved to src/plugins/theme-glass.css.

   It was ~50 rules of the form `.dark-mode .section-card { … !important }`,
   i.e. only the DARK half of each element, with the light half left in ~36
   scoped page blocks. That split is what made the two drift, and the
   !important is what made a page unable to opt out: the file's own comments
   record two separate bugs caused by exactly that (the sky gradient that kept
   coming back on application/View.vue, and the amber/purple header modifiers
   being flattened to accent blue).

   The glass layer states each element ONCE, in tokens that flip themselves,
   at `#app …` specificity and with no !important — so a page can still
   override it. Nothing theme-specific belongs in this file any more; what
   remains above is the navbar and the layout geometry it owns.
   ============================================ */

</style>
